import React from "react";
import {
  Box,
  Button,
  Paper,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import SwapHorizRounded from "@mui/icons-material/SwapHorizRounded";
import ReceiptLongRounded from "@mui/icons-material/ReceiptLongRounded";

/** Балансы — деньги, поэтому две значащие цифры после запятой и моноширинные цифры. */
export const money = (value: number | null | undefined) =>
  Number(value ?? 0).toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

type Slice = { key: string; label: string; value: number; color: string };

/**
 * Счёт одного мерчанта.
 *
 * Читается как строка реестра, а не как рекламная карточка: сверху итог, под
 * ним полоса долей, ниже три составляющих в колонках с выравниванием по разряду.
 * Действия названы словами — четыре разноцветные иконки без подписей не говорят,
 * чем «перестановка» отличается от «вывода».
 */
const MerchantBalanceCard: React.FC<{
  name: string;
  symbol: string;
  deposit: number;
  payout: number;
  frozen: number;
  onDeposit: () => void;
  onWithdraw: () => void;
  onMoveToPayout: () => void;
  onHistory: () => void;
}> = ({
  name,
  symbol,
  deposit,
  payout,
  frozen,
  onDeposit,
  onWithdraw,
  onMoveToPayout,
  onHistory,
}) => {
  const theme = useTheme();
  const total = deposit + payout + frozen;

  const slices: Slice[] = [
    {
      key: "deposit",
      label: "Депозитный",
      value: deposit,
      color: theme.palette.primary.main,
    },
    {
      key: "payout",
      label: "Выплатной",
      value: payout,
      color: theme.palette.success.main,
    },
    {
      key: "frozen",
      label: "Заморожено",
      value: frozen,
      color: theme.palette.warning.main,
    },
  ];

  const textTone: Record<string, string> = {
    deposit: theme.palette.accentText,
    payout: theme.palette.semanticText.success,
    frozen: theme.palette.semanticText.warning,
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: "18px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        transition: "transform 180ms ease, border-color 180ms ease",
        "&:hover": {
          transform: "translateY(-3px)",
          borderColor: alpha(theme.palette.primary.main, 0.4),
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontSize: 16, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {name}
        </Typography>
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 740,
            letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {money(total)}
          <Typography
            component="span"
            color="text.secondary"
            sx={{ fontSize: 13, fontWeight: 700, ml: 0.5 }}
          >
            {symbol}
          </Typography>
        </Typography>
      </Box>

      {/* Доли трёх составляющих: видно перекос, не читая цифр. */}
      <Box
        sx={{
          display: "flex",
          height: 6,
          borderRadius: 999,
          overflow: "hidden",
          gap: "2px",
          backgroundColor: alpha(theme.palette.text.primary, 0.06),
        }}
      >
        {total > 0 &&
          slices.map((slice) => (
            <Tooltip
              key={slice.key}
              title={`${slice.label}: ${Math.round((slice.value / total) * 100)}%`}
            >
              <Box
                sx={{
                  width: `${(slice.value / total) * 100}%`,
                  backgroundColor: slice.color,
                }}
              />
            </Tooltip>
          ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 1.5,
        }}
      >
        {slices.map((slice) => (
          <Box key={slice.key} sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  flexShrink: 0,
                  borderRadius: "50%",
                  backgroundColor: slice.color,
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11, fontWeight: 650 }}
                noWrap
              >
                {slice.label}
              </Typography>
            </Box>
            <Typography
              sx={{
                mt: 0.25,
                fontSize: 15,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                color: textTone[slice.key],
              }}
              noWrap
            >
              {money(slice.value)}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          pt: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddRounded />}
          onClick={onDeposit}
          sx={{ minHeight: 34 }}
        >
          Пополнить
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<RemoveRounded />}
          onClick={onWithdraw}
          sx={{ minHeight: 34 }}
        >
          Вывести
        </Button>
        <Tooltip title="Перевести с депозитного баланса на выплатной">
          <Button
            size="small"
            variant="outlined"
            startIcon={<SwapHorizRounded />}
            onClick={onMoveToPayout}
            sx={{ minHeight: 34 }}
          >
            На выплату
          </Button>
        </Tooltip>
        <Tooltip title="История сетлов">
          <Button
            size="small"
            variant="text"
            startIcon={<ReceiptLongRounded />}
            onClick={onHistory}
            sx={{ minHeight: 34, ml: "auto" }}
          >
            История
          </Button>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default MerchantBalanceCard;
